/**
 * The Constellations panel: a button in the graph's top-right corner that
 * opens a guide to the constellations, and a collection to complete.
 *
 * Every lesson takes the shape of a real constellation (`skyFigures`), its
 * key ideas as the stars. A constellation is COLLECTED once you have learned
 * every key idea of a lesson that takes it, so its whole figure is lit on
 * the map. The panel explains each constellation and shows which you have
 * collected, with the goal of collecting them all.
 *
 * Desktop only, like the Legend: on a phone the top-right corner holds the
 * fit and guide buttons.
 */

import * as Popover from '@radix-ui/react-popover';
import { Check, X } from 'lucide-react';
import { SKY_FIGURES, type SkyFigure } from '@/lib/mindspace/skyFigures';

/** Stars learned out of a figure's stars, for the best lesson taking it. */
export interface ConstellationProgress {
  learned: number;
  total: number;
}

type Standing = 'collected' | 'found' | 'missing';

export function ConstellationGuide({
  progress,
}: {
  /** Per constellation on your map, the best lesson's progress on it. */
  progress: ReadonlyMap<string, ConstellationProgress>;
}) {
  const total = SKY_FIGURES.length;
  const standing = (name: string): Standing => {
    const p = progress.get(name);
    if (!p) return 'missing';
    return p.learned >= p.total ? 'collected' : 'found';
  };
  const found = SKY_FIGURES.filter(f => standing(f.name) !== 'missing').length;
  const collected = SKY_FIGURES.filter(
    f => standing(f.name) === 'collected'
  ).length;
  // Collected, then on your map (closest to collecting first), then the rest.
  const order: Record<Standing, number> = {
    collected: 0,
    found: 1,
    missing: 2,
  };
  const share = (name: string) => {
    const p = progress.get(name);
    return p ? p.learned / p.total : 0;
  };
  const sorted = [...SKY_FIGURES].sort(
    (a, b) =>
      order[standing(a.name)] - order[standing(b.name)] ||
      share(b.name) - share(a.name) ||
      a.stars.length - b.stars.length
  );

  return (
    <Popover.Root>
      <Popover.Trigger className='absolute right-6 top-6 inline-flex h-8 items-center gap-2 rounded-lg border border-border bg-card/90 px-3 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'>
        <ConstellationIcon className='h-4 w-4' />
        Constellations
        <span className='font-mono tabular-nums text-foreground/70'>
          {found} found
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side='bottom'
          align='end'
          sideOffset={8}
          collisionPadding={16}
          aria-label='Constellations'
          className='z-40 flex max-h-[min(70vh,600px)] w-96 max-w-[calc(100vw-2rem)] flex-col rounded-lg border border-border bg-card text-left shadow-elevation-2 motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in-0'
        >
          <GuideHeader collected={collected} found={found} total={total} />
          {/* Focusable so a keyboard can scroll it (it outgrows the panel):
              axe's scrollable-region-focusable requires it, which overrides
              jsx-a11y's general rule against tabIndex on a list. */}
          <ul
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            aria-label='All constellations'
            className='divide-y divide-border overflow-y-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
          >
            {sorted.map(figure => (
              <ConstellationRow
                key={figure.name}
                figure={figure}
                where={standing(figure.name)}
                progress={progress.get(figure.name)}
              />
            ))}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** One constellation: its drawing, name, your standing on it and a note. */
function ConstellationRow({
  figure,
  where,
  progress: p,
}: {
  figure: SkyFigure;
  where: Standing;
  progress?: ConstellationProgress;
}) {
  return (
    <li className='flex gap-3 p-4'>
      <FigureSketch figure={figure} standing={where} />
      <div className='min-w-0 flex-1'>
        <p className='flex items-center gap-2 text-sm font-medium text-foreground'>
          {figure.name}
          {where === 'collected' ? (
            <span className='inline-flex items-center gap-1 rounded-full bg-brand-50 px-1.5 py-0.5 text-[11px] font-medium text-brand-700 dark:bg-muted dark:text-primary'>
              <Check className='h-3 w-3' aria-hidden='true' />
              Collected
            </span>
          ) : where === 'found' && p ? (
            <span className='font-mono text-[11px] font-normal tabular-nums text-muted-foreground'>
              {p.learned} of {p.total} stars learned
            </span>
          ) : (
            <span className='text-[11px] font-normal text-muted-foreground'>
              Not on your map yet
            </span>
          )}
        </p>
        <p className='mt-0.5 text-xs leading-relaxed text-muted-foreground'>
          {figure.note}
        </p>
      </div>
    </li>
  );
}

/** The panel's top: the goal, the reading key and your progress. */
function GuideHeader({
  collected,
  found,
  total,
}: {
  collected: number;
  found: number;
  total: number;
}) {
  return (
    <div className='border-b border-border p-4'>
      <div className='flex items-start justify-between gap-3'>
        <h2 className='text-sm font-semibold text-foreground'>
          Collect them all
        </h2>
        <Popover.Close
          aria-label='Close'
          className='-mr-1 -mt-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <X className='h-4 w-4' aria-hidden='true' />
        </Popover.Close>
      </div>
      <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
        Every lesson takes the shape of a real constellation, with its key ideas
        as the stars. Learn every key idea in a lesson and its constellation
        lights up and joins your collection.
      </p>
      <HowToRead />
      <div className='mt-3 flex items-center gap-3'>
        <div
          className='h-1.5 flex-1 overflow-hidden rounded-full bg-muted'
          role='progressbar'
          aria-label='Constellations collected'
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={collected}
        >
          <div
            className='h-full rounded-full bg-brand-600 dark:bg-primary'
            style={{ width: `${(collected / total) * 100}%` }}
          />
        </div>
        <span className='font-mono text-xs tabular-nums text-muted-foreground'>
          {collected} of {total} collected
        </span>
      </div>
      <p className='mt-1.5 text-xs text-muted-foreground'>
        {found} of {total} are on your map so far.
      </p>
    </div>
  );
}

/**
 * How to read a constellation on the map: what each kind of line means, and
 * what "on your map" and "collected" mean. Each line is drawn as it is on the
 * map, so the key matches what the reader is looking at.
 */
function HowToRead() {
  const lines: Array<{ dashed?: boolean; faint?: boolean; text: string }> = [
    { text: 'You have learned both ideas it joins.' },
    { faint: true, text: 'One of them is fading or worth a review.' },
    { dashed: true, text: 'One of them is not studied yet.' },
  ];
  return (
    <div className='mt-3 rounded-md border border-border bg-muted/40 p-3'>
      <h3 className='font-mono text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>
        How to read them
      </h3>
      <ul className='mt-2 space-y-1.5'>
        {lines.map(line => (
          <li
            key={line.text}
            className='flex items-center gap-2.5 text-xs text-muted-foreground'
          >
            <svg
              viewBox='0 0 28 8'
              className='h-2 w-7 flex-shrink-0'
              aria-hidden='true'
            >
              <circle
                cx={3}
                cy={4}
                r={2}
                className='fill-brand-600 dark:fill-primary'
              />
              <circle
                cx={25}
                cy={4}
                r={2}
                className='fill-brand-600 dark:fill-primary'
              />
              <line
                x1={5}
                y1={4}
                x2={23}
                y2={4}
                className='stroke-brand-600 dark:stroke-primary'
                strokeWidth={line.faint ? 1 : 1.5}
                strokeDasharray={line.dashed ? '2 2.5' : undefined}
                opacity={line.faint ? 0.45 : line.dashed ? 0.6 : 0.9}
              />
            </svg>
            {line.text}
          </li>
        ))}
      </ul>
      <dl className='mt-2.5 space-y-1 border-t border-border pt-2.5 text-xs text-muted-foreground'>
        <div>
          <dt className='inline font-medium text-foreground'>On your map:</dt>{' '}
          <dd className='inline'>
            one of your lessons takes this shape. The count shows how many of
            its stars you have learned.
          </dd>
        </div>
        <div>
          <dt className='inline font-medium text-foreground'>Collected:</dt>{' '}
          <dd className='inline'>
            every line is solid, because you have learned all of its stars.
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * The panel's icon: the Big Dipper, seven stars and their lines, in the
 * button's text colour. Lucide has no constellation icon, and a generic
 * sparkle said "magic" rather than "the night sky".
 */
function ConstellationIcon({ className }: { className?: string }) {
  const stars: Array<[number, number]> = [
    [18.5, 8.5],
    [18.5, 13],
    [12.5, 13.5],
    [11.5, 9.5],
    [8, 8.5],
    [5, 8.5],
    [1.5, 10.5],
  ];
  const path = [3, 0, 1, 2, 3, 4, 5, 6]
    .map((i, k) => `${k === 0 ? 'M' : 'L'}${stars[i][0]} ${stars[i][1]}`)
    .join(' ');
  return (
    <svg viewBox='0 0 20 20' className={className} aria-hidden='true'>
      <path
        d={path}
        fill='none'
        stroke='currentColor'
        strokeWidth={1}
        strokeLinejoin='round'
        opacity={0.6}
      />
      {stars.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={1.5} fill='currentColor' />
      ))}
    </svg>
  );
}

/** A small drawing of a constellation's figure: lit once collected, clear
 *  but dashed while on your map, faded while it is not. */
function FigureSketch({
  figure,
  standing,
}: {
  figure: SkyFigure;
  standing: Standing;
}) {
  const lit = standing === 'collected';
  const at = (v: number) => 28 + v * 22;
  return (
    <svg
      viewBox='0 0 56 56'
      className={`h-14 w-14 flex-shrink-0 rounded-md bg-muted/60 ${standing === 'missing' ? 'opacity-40' : ''}`}
      aria-hidden='true'
    >
      {figure.lines.map(([a, b]) => (
        <line
          key={`${a}-${b}`}
          x1={at(figure.stars[a][0])}
          y1={at(figure.stars[a][1])}
          x2={at(figure.stars[b][0])}
          y2={at(figure.stars[b][1])}
          className='stroke-brand-600/60 dark:stroke-primary/60'
          strokeWidth={1}
          strokeDasharray={lit ? undefined : '2 2'}
        />
      ))}
      {figure.stars.map(([x, y], i) => (
        <circle
          key={i}
          cx={at(x)}
          cy={at(y)}
          r={i === 0 ? 2.6 : 1.8}
          className='fill-brand-600 dark:fill-primary'
        />
      ))}
    </svg>
  );
}
